/**
 * FlowEdges — das SVG-Overlay zu `useFlowEdges`.
 *
 * Liegt absolut über dem Canvas (`position: relative` beim Canvas ist
 * Voraussetzung), ist `pointer-events: none` und liegt unter den Knoten.
 * Ruhe-Regel: Kanten stehen dauerhaft in `--prn-label-4`; erst Zeigen oder
 * Tabben auf einem Knoten hebt SEINE Kanten auf Akzent (`highlight`).
 * Abgeleitete Kanten sind gestrichelt, damit die Rateschritte sichtbar bleiben.
 *
 * Ausnahme mit Absicht: **bahninterne** Kanten (beide Enden in derselben Bahn)
 * bekommen Schaft und Pfeilspitze statt Bézier und Punkt und stehen eine Stufe
 * kräftiger — sie sind die einzigen, deren Aussage die RICHTUNG ist („X füllt
 * Y"). Die 15 Bahn-zu-Bahn-Kanten bleiben unverändert ruhig. Begründung und
 * Erkennungsregel: `stackedEdges.ts`.
 */
import { useEffect, useRef, type RefObject } from "react";
import { cx } from "@conuti-das/prince-ui";
import { useFlowEdges, type FlowEdgeSpec, type FlowEdgesGeometry } from "./useFlowEdges";
import { useFlowRelations } from "./stackedEdges";
import "./flow.css";

export interface FlowEdgesProps {
  canvasRef: RefObject<HTMLElement | null>;
  edges: FlowEdgeSpec[];
  selector?: string;
  enabled?: boolean;
  /** Knoten-ID, deren Kanten hervorgehoben werden. */
  highlight?: string | null;
  /** Rückmeldung der gemessenen Geometrie (für „n Kanten · m abgeleitet"). */
  onGeometry?: (geometry: FlowEdgesGeometry) => void;
  className?: string;
}

export function FlowEdges({
  canvasRef,
  edges,
  selector,
  enabled = true,
  highlight,
  onGeometry,
  className,
}: FlowEdgesProps) {
  const geometry = useFlowEdges({ canvasRef, edges, selector, enabled });
  // Die Beziehung als Wort — läuft AUCH, wenn nichts gezeichnet wird (Zoomstufe
  // „Übersicht", gestapeltes Layout). `drawn` sagt dem Hook, ob die Richtung
  // schon auf dem Schirm steht; sonst setzt er den Marker am Knoten.
  useFlowRelations({ canvasRef, edges, selector, drawn: geometry.stacked > 0 });

  // Rückmeldung erst nach dem Commit — ein Aufruf im Render würde den Zähler
  // der Oberfläche mitten in unserem Render setzen.
  const report = useRef(onGeometry);
  report.current = onGeometry;
  useEffect(() => {
    report.current?.(geometry);
  }, [geometry]);

  if (!geometry.width) return null;

  return (
    <svg
      className={cx("flow-edges", className)}
      viewBox={`0 0 ${geometry.width} ${geometry.height}`}
      width={geometry.width}
      height={geometry.height}
      data-hl={highlight ?? undefined}
      aria-hidden="true"
      focusable="false"
    >
      {geometry.paths.map((p) => {
        const on = !!highlight && (p.from === highlight || p.to === highlight);
        return (
          <g key={p.key}>
            <path
              className={cx(p.derived && "is-derived", p.stacked && "is-stacked", on && "is-hl")}
              d={p.d}
            />
            {/* Pfeilspitze statt Punkt: bahnintern ist die Richtung die Aussage. */}
            {p.arrow ? (
              <path className={cx("flow-edge-arrow", on && "is-hl")} d={p.arrow} />
            ) : (
              <circle cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="1.8" />
            )}
          </g>
        );
      })}
    </svg>
  );
}
